import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Calendar, User, Tag, Share2, Facebook, Twitter, Linkedin, Link2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BlogArticlePage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (slug) {
      fetchArticle();
      fetchRelated();
    }
  }, [slug]);

  // Set document meta tags for SEO
  useEffect(() => {
    if (article) {
      document.title = article.meta_title || article.title;
      setMetaTag('description', article.meta_description || article.excerpt || '');
      setMetaTag('og:title', article.meta_title || article.title, 'property');
      setMetaTag('og:description', article.meta_description || article.excerpt || '', 'property');
      setMetaTag('og:type', 'article', 'property');
      setMetaTag('og:url', window.location.href, 'property');
      if (article.og_image || article.featured_image) {
        setMetaTag('og:image', article.og_image || article.featured_image, 'property');
      }
    }
    return () => { document.title = 'Exam Ready English'; };
  }, [article]);

  const setMetaTag = (name, content, attr = 'name') => {
    let el = document.querySelector(`meta[${attr}="${name}"]`);
    if (!el) {
      el = document.createElement('meta');
      el.setAttribute(attr, name);
      document.head.appendChild(el);
    }
    el.setAttribute('content', content);
  };

  const fetchArticle = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/blog/articles/${slug}`);
      setArticle(res.data);
    } catch (error) {
      console.error('Error fetching article:', error);
      if (error.response?.status === 404) navigate('/blog');
    } finally {
      setLoading(false);
    }
  };

  const fetchRelated = async () => {
    try {
      const res = await axios.get(`${API}/blog/articles/${slug}/related?limit=3`);
      setRelated(res.data);
    } catch (error) {
      console.error('Error fetching related:', error);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric', month: 'long', year: 'numeric'
    });
  };

  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';

  const share = (platform) => {
    const title = encodeURIComponent(article?.title || '');
    const url = encodeURIComponent(shareUrl);
    const urls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}`,
      twitter: `https://twitter.com/intent/tweet?text=${title}&url=${url}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${url}`,
    };
    if (urls[platform]) window.open(urls[platform], '_blank', 'width=600,height=400');
  };

  const copyLink = () => {
    navigator.clipboard.writeText(shareUrl);
    toast.success('Link copied!');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7]">
        <div className="max-w-3xl mx-auto px-4 py-20 animate-pulse space-y-6">
          <div className="h-8 bg-slate-200 rounded w-2/3" />
          <div className="h-64 bg-slate-200 rounded" />
          <div className="space-y-3">
            <div className="h-4 bg-slate-200 rounded" />
            <div className="h-4 bg-slate-200 rounded w-5/6" />
            <div className="h-4 bg-slate-200 rounded w-4/6" />
          </div>
        </div>
      </div>
    );
  }

  if (!article) return null;

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-16">
            <button
              onClick={() => navigate('/blog')}
              className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors"
              data-testid="article-back-btn"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="text-sm">Blog</span>
            </button>
            <span className="font-['Playfair_Display'] text-xl font-bold text-slate-900">Blog</span>
            <div className="w-16" />
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
        {/* Categories */}
        {article.categories?.length > 0 && (
          <div className="flex gap-2 mb-4">
            {article.categories.map(cat => (
              <button
                key={cat}
                onClick={() => navigate(`/blog?category=${encodeURIComponent(cat)}`)}
                className="text-xs font-medium text-orange-600 bg-orange-50 px-3 py-1 rounded-full hover:bg-orange-100 transition-colors"
              >
                {cat}
              </button>
            ))}
          </div>
        )}

        {/* Title */}
        <h1 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-bold text-slate-900 mb-4 leading-tight" data-testid="article-title">
          {article.title}
        </h1>

        {/* Meta */}
        <div className="flex items-center gap-4 text-sm text-slate-500 mb-8">
          {article.author && (
            <span className="flex items-center gap-1.5">
              <User className="w-4 h-4" />
              {article.author}
            </span>
          )}
          {article.published_at && (
            <span className="flex items-center gap-1.5">
              <Calendar className="w-4 h-4" />
              {formatDate(article.published_at)}
            </span>
          )}
        </div>

        {/* Featured Image */}
        {article.featured_image && (
          <div className="mb-8 rounded-lg overflow-hidden">
            <img
              src={article.featured_image}
              alt={article.title}
              className="w-full h-auto object-cover"
              data-testid="article-featured-image"
            />
          </div>
        )}

        {/* Content */}
        <article
          className="prose prose-slate prose-lg max-w-none mb-10
            prose-headings:font-['Playfair_Display'] prose-headings:text-slate-900
            prose-a:text-orange-600 prose-a:no-underline hover:prose-a:underline
            prose-img:rounded-lg"
          dangerouslySetInnerHTML={{ __html: article.content }}
          data-testid="article-content"
        />

        {/* Tags */}
        {article.tags?.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 mb-8 pt-6 border-t border-slate-200">
            <Tag className="w-4 h-4 text-slate-400" />
            {article.tags.map(tag => (
              <button
                key={tag}
                onClick={() => navigate(`/blog?tag=${encodeURIComponent(tag)}`)}
                className="text-xs text-slate-500 bg-slate-100 px-3 py-1 rounded-full hover:bg-slate-200 transition-colors"
              >
                {tag}
              </button>
            ))}
          </div>
        )}

        {/* Share */}
        <div className="flex items-center gap-3 mb-12 pt-4 border-t border-slate-200" data-testid="article-share">
          <span className="text-sm font-medium text-slate-600 flex items-center gap-1.5">
            <Share2 className="w-4 h-4" /> Share
          </span>
          <button onClick={() => share('facebook')} className="w-9 h-9 rounded-full bg-[#1877F2] text-white flex items-center justify-center hover:opacity-80 transition-opacity" aria-label="Share on Facebook">
            <Facebook className="w-4 h-4" />
          </button>
          <button onClick={() => share('twitter')} className="w-9 h-9 rounded-full bg-[#1DA1F2] text-white flex items-center justify-center hover:opacity-80 transition-opacity" aria-label="Share on Twitter">
            <Twitter className="w-4 h-4" />
          </button>
          <button onClick={() => share('linkedin')} className="w-9 h-9 rounded-full bg-[#0A66C2] text-white flex items-center justify-center hover:opacity-80 transition-opacity" aria-label="Share on LinkedIn">
            <Linkedin className="w-4 h-4" />
          </button>
          <button onClick={copyLink} className="w-9 h-9 rounded-full bg-slate-200 text-slate-600 flex items-center justify-center hover:bg-slate-300 transition-colors" aria-label="Copy link">
            <Link2 className="w-4 h-4" />
          </button>
        </div>

        {/* Related Articles */}
        {related.length > 0 && (
          <div className="pt-8 border-t border-slate-200" data-testid="related-articles">
            <h2 className="font-['Playfair_Display'] text-2xl font-bold text-slate-900 mb-6">Related Articles</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              {related.map(item => (
                <article
                  key={item.id}
                  onClick={() => navigate(`/blog/${item.slug}`)}
                  className="bg-white border border-slate-200 rounded-lg overflow-hidden cursor-pointer group hover:shadow-md transition-shadow"
                >
                  {item.featured_image ? (
                    <div className="h-32 overflow-hidden">
                      <img src={item.featured_image} alt={item.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                    </div>
                  ) : (
                    <div className="h-32 bg-gradient-to-br from-orange-100 to-amber-50 flex items-center justify-center">
                      <span className="font-['Playfair_Display'] text-2xl text-orange-300 font-bold">ER</span>
                    </div>
                  )}
                  <div className="p-4">
                    <h3 className="text-sm font-bold text-slate-900 line-clamp-2 group-hover:text-orange-700 transition-colors">
                      {item.title}
                    </h3>
                    {item.published_at && (
                      <p className="text-xs text-slate-400 mt-2">{formatDate(item.published_at)}</p>
                    )}
                  </div>
                </article>
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
